import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useRouter } from 'next/router';
import { normalizeText } from '@portal-internet/core';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import { useViewport } from 'hooks/3catinfo/useViewport';
import Icona from 'components/3catinfo/ui/Icona';
import Enllac from 'components/3catinfo/Enllac';
import __isEmpty from 'lodash/isEmpty';
import municipis from './tendencia_municipis.json';
import styles from './cercaTendenciaMunicipi.module.scss';
import clsx from 'clsx';

const filterMunicipi = (searchValue) => {
    return municipis.municipis.filter(({ nom }) =>
        normalizeText(nom)?.toLowerCase().includes(normalizeText(searchValue).toLowerCase())
    );
};

const ItemResultatCerca = forwardRef((props, publicRef) => {
    const { item, index, handleKeyDown } = props;
    const ref = useRef();
    const { nom, id } = item;

    useImperativeHandle(publicRef, () => ({
        focus() {
            ref.current.focus();
        }
    }));

    return (
        <li className={styles.itemResultat} ref={ref} tabIndex={index} onKeyDown={handleKeyDown}>
            <Enllac
                type='web_3catinfo_tendencies_climatiques_municipi'
                slug={nom}
                id={id}
                className={styles.municipi}
                title={`Tendencia climàtica a ${nom}`}
            >
                {nom}
            </Enllac>
        </li>
    );
});

const Resultats = (props) => {
    const { items, searching, setSearching, selectedIndex, handleKeyDown } = props;
    const divRef = useRef(null);
    const [itemRefs, setItemRefs] = useState([]);

    const handleClickOutside = (event) => {
        if (divRef.current && !divRef.current.contains(event.target)) {
            setSearching(false);
        }
    };

    useEffect(() => {
        if (selectedIndex >= 0 && itemRefs[selectedIndex]) {
            itemRefs[selectedIndex].current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    useEffect(() => {
        setItemRefs((elRefs) =>
            Array(items.length)
                .fill()
                .map((_, i) => elRefs[i] || React.createRef())
        );
    }, [items]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!searching) return <></>;
    return (
        <div className={styles.wrapperResultats} ref={divRef}>
            <ul className={styles.contentResultats}>
                {__isEmpty(items) ? (
                    <li className={styles.noResults}>No s&apos;han trobat resultats</li>
                ) : (
                    items.map((item, index) => (
                        <ItemResultatCerca
                            key={`cercador-item-resultat-${item.id}`}
                            ref={itemRefs[index]}
                            item={item}
                            index={index}
                            handleKeyDown={handleKeyDown}
                        />
                    ))
                )}
            </ul>
        </div>
    );
};

const CercaTendenciaMunicipi = (props) => {
    const { placeholder, estil, estilIcon } = props;
    const router = useRouter();
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [searching, setSearching] = useState(false);
    const updateCercaTimer = useRef(null);
    const inputRef = useRef(null);
    const debounceTimer = 300;
    const { zoomNavigator } = useViewport();

    const handleSubmit = (e) => {
        e.preventDefault();
        const municipisFilterList = filterMunicipi(searchValue);

        if (searchValue.length >= 2 && !__isEmpty(municipisFilterList)) {
            router.push(
                getUrlFriendly({
                    routePath: 'web_3catinfo_tendencies_climatiques_municipi',
                    id: municipisFilterList[selectedIndex === -1 ? 0 : selectedIndex].id,
                    slug: municipisFilterList[selectedIndex === -1 ? 0 : selectedIndex].nom,
                    producte: 'web_noticies'
                })
            );
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, searchResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            setSelectedIndex((prevIndex) => {
                if (prevIndex === 0) {
                    inputRef.current.focus();
                    requestAnimationFrame(() => {
                        const length = inputRef.current.value.length;
                        inputRef.current.setSelectionRange(length, length);
                    });
                }
                return Math.max(prevIndex - 1, -1);
            });
        } else if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    useEffect(() => {
        clearTimeout(updateCercaTimer.current);

        if (searchValue.length >= 2) {
            updateCercaTimer.current = setTimeout(() => {
                setSelectedIndex(-1);
                setSearchResults(filterMunicipi(searchValue));
                setSearching(true);
            }, debounceTimer);
        } else {
            setSearching(false);
        }
    }, [searchValue]);

    const stylesSearchInput = clsx({
        [styles.searchInput]: true,
        [styles.ambZoom]: zoomNavigator > 3
    });

    const stylesButton = clsx({
        [styles.searchButton]: true,
        [styles[estil]]: !!estil,
        [styles[estilIcon]]: !!estilIcon
    });
    return (
        <div className={styles.innerSearcher}>
            <form onSubmit={handleSubmit} className={styles.searchForm}>
                <div className={styles.contentInput}>
                    <label htmlFor='search' className='sr-only'>
                        Buscar:
                    </label>
                    <input
                        ref={inputRef}
                        type='search'
                        id='search'
                        name='search'
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        required
                        className={stylesSearchInput}
                        aria-label={placeholder}
                        autoComplete='off'
                        data-testid='cerca-un-municipi-input'
                        aria-haspopup='listbox'
                        aria-autocomplete='list'
                    />
                    <button type='submit' className={stylesButton} aria-label='buscar'>
                        <Icona className={styles.icona} icona='cercador_ple' isBlanc={false} />
                    </button>
                </div>
                <Resultats
                    searching={searching}
                    searchValue={searchValue}
                    items={searchResults}
                    selectedIndex={selectedIndex}
                    setSearching={setSearching}
                    handleKeyDown={handleKeyDown}
                />
            </form>
        </div>
    );
};

CercaTendenciaMunicipi.defaultProps = {
    placeholder: 'Cerca'
};

export default CercaTendenciaMunicipi;

ItemResultatCerca.displayName = 'ItemResultatCerca';
